/*
 * @Author: Ryan
 * @Date: 2026-01-19
 * @Description: Testimonials section - Trusted by E-Commerce Brands Worldwide
 */
import React from 'react';

interface Testimonial {
  title: string;
  content: string;
  role: string;
  name: string;
}

const testimonials: Testimonial[] = [
  {
    title: 'Finally, a Way to Make Videos Without a Camera',
    content:
      "I used to spend days coordinating with creators just to get one product demo filmed. Half the time, the footage didn't match my brand or I needed reshoots. With FalcoCut, I upload a product photo, pick an avatar, and the video is ready in minutes. It's the first time video creation has actually felt stress-free.",
    role: 'Shopify Store Owner',
    name: 'Daniel Hughes',
  },
  {
    title: 'Cut My Production Costs More Than I Expected',
    content:
      'Paying creators for every new product launch was eating up my margins. Even simple demos cost hundreds. FalcoCut changed everything — now I generate product videos for a fraction of the price, and I can test multiple versions without worrying about budget.',
    role: 'Amazon Seller',
    name: 'Sarah Mitchell',
  },
  {
    title: 'Make My Brand Look More Professional',
    content:
      "Working with different creators meant every video had a different tone, style, and energy. It made my brand feel scattered. FalcoCut's avatars give me a consistent presenter across all my product lines, and the videos look polished every time.",
    role: 'Marketing Manager',
    name: 'Lauren Matthews',
  },
  {
    title: 'Localization Used to Be a Nightmare — Not Anymore',
    content:
      'I sell in multiple countries, and hiring creators for each language was impossible to manage. FalcoCut lets me generate the same product demo in different languages with natural-sounding voices. Now I can launch globally without extra production work.',
    role: 'Ecommerce Operator',
    name: 'Javier Morales',
  },
  {
    title: 'Update Videos Instantly When My Product Changes',
    content:
      'Whenever packaging or features changed, I had to redo entire shoots. It was slow and expensive. With FalcoCut, I just swap the product image or adjust the script and regenerate the video. What used to take days now takes minutes.',
    role: 'Product Manager',
    name: 'Olivia Bennett',
  },
  {
    title: 'Perfect for Rapid Testing and Daily Content Needs',
    content:
      "I run a lot of paid ads, and testing new hooks used to be painful because creators couldn't deliver fast enough. FalcoCut lets me generate multiple product avatar variations instantly. I've never been able to test this quickly before.",
    role: 'YouTube Creator',
    name: 'Marcus Lee',
  },
];

const StarRating: React.FC = () => (
  <div className="flex gap-1 mb-3 pad:mb-4">
    {[1, 2, 3, 4, 5].map((star) => (
      <svg
        key={star}
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 1L12.39 6.26L18 7.27L14 11.14L14.76 17L10 14.27L5.24 17L6 11.14L2 7.27L7.61 6.26L10 1Z"
          fill="#FBBF24"
          stroke="#FBBF24"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    ))}
  </div>
);

const TestimonialCard: React.FC<Testimonial> = ({ title, content, role, name }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 h-full flex flex-col">
      <StarRating />
      <h3 className="text-lg pad:text-xl font-bold text-gray-900 mb-3 pad:mb-4">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed flex-grow mb-4 pad:mb-6">
        {content}
      </p>
      <div>
        <p className="font-semibold text-gray-900 text-sm pad:text-base">{role}</p>
        <p className="text-gray-500 text-sm">{name}</p>
      </div>
    </div>
  );
};

const TestimonialsSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          What Our Customers Say
        </h2>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 sm:grid-cols-3 gap-4 pad:gap-6">
          {testimonials.map((testimonial, index) => (
            <TestimonialCard
              key={index}
              title={testimonial.title}
              content={testimonial.content}
              role={testimonial.role}
              name={testimonial.name}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
